import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import {
  LIST_POLICY_ALIASES,
  LIST_USER_SECURITY_POLICIES,
  UPDATE_USER_SECURITY_POLICIES
} from '../queries';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, StarOff } from 'lucide-react';

type AliasRecord = {
  id: number;
  alias: string;
  type?: string | null;
  description?: string | null;
  active?: boolean | null;
  policies?: string[] | null;
};

type UserPolicyAssignment = {
  policy: string;
  allow?: boolean | null;
};

type AliasType = 'group' | 'policy' | 'legacy' | 'hidden' | 'organizational';
type PolicyCategory = 'group' | 'policy' | 'direct';

const visibleAliasTypes = new Set<AliasType>(['group', 'policy']);
const LEGACY_ROLE_ALIAS_VALUES = new Set([
  'admin',
  'court',
  'limited',
  'provider',
  'provider+',
  'providercen',
  'providerr',
  'superadmin'
]);
const TOOL_ALIAS_DOC_LIB_EXTERNAL = 'Doc Library External';
const TOOL_ALIAS_DOC_LIB_INTERNAL = 'Doc Library Internal';
const TOOL_ALIAS_DOC_LIB_ADMIN = 'Doc Library Admin';
const TOOL_ALIAS_LEGACY_DOCUMENTS_LIBRARY = 'Documents Library';
const TOOL_ALIAS_LEGACY_DOCUMENTS_LIBRARY_WRITE = 'Documents Library (write)';
const TOOL_ALIAS_LAI_JAIL = 'LAI Jail';
const TOOL_ALIAS_LAI_JAIL_WRITE = 'LAI Jail (write)';
const TOOL_ALIAS_MEDCALL_STANDARD = 'MedCall Standard';
const TOOL_ALIAS_LEGACY_MEDCALL = 'MedCall';
const TOOL_ALIAS_MEDCALL_POWER_USER = 'MedCall Power User';
const TOOL_ALIAS_NOTIFICATION_LOG = 'Notification Log';
const TOOL_ALIAS_LEGACY_NOTIFICATION_LOG = 'notifications';
const TOOL_ALIAS_PUBLIC_DEFENDERS_LIST = 'Public Defenders List';
const TOOL_ALIAS_REFERRAL_INTAKE = 'Referral Intake';
const TOOL_ALIAS_LEGACY_REFERRAL_TOOL = 'Referral Tool';
const TOOL_ALIAS_USER_ACCOUNT_REQUESTS = 'Request User Access';
const TOOL_GROUP_DOC_LIB_EXTERNAL = 'tools:docslib:external';
const TOOL_GROUP_DOC_LIB_INTERNAL = 'tools:docslib:internal';
const TOOL_GROUP_DOC_LIB_ADMIN = 'tools:docslib:admin';
const TOOL_GROUP_LAI_JAIL_READ = 'tools:lai:jail:read';
const TOOL_GROUP_LAI_JAIL_WRITE = 'tools:lai:jail:write';
const TOOL_GROUP_MEDCALL_STANDARD = 'tools:medcall:access';
const TOOL_GROUP_MEDCALL_POWER_USER = 'tools:medcall:power';
const TOOL_GROUP_NOTIFICATION_LOG = 'tools:notifications';
const TOOL_GROUP_NOTIFICATIONS_VIA_OUTLOOK = 'tools:notificationsviaoutlook';
const TOOL_GROUP_PUBLIC_DEFENDERS_LIST = 'tools:publicdefenderslist';
const TOOL_GROUP_LEGACY_REFERRAL_TOOL = 'tools:referraltool';
const TOOL_GROUP_REFERRAL_INTAKE = TOOL_GROUP_PUBLIC_DEFENDERS_LIST;
const TOOL_GROUP_USER_ACCOUNT_REQUESTS = 'tools:requestuser';
const TOOL_GROUP_BILLING = 'tools:billing:access';
const TOOL_GROUP_BILLING_ADMIN = 'tools:billing:admin';
const TOOL_GROUP_SUPPORT_TICKETS = 'tools:support:tickets';
const TOOL_GROUP_DRUGBANK = 'tools:drugbank';
const META_ECM_LCM = 'meta:ecm:lcm';
const META_ECM_QA = 'meta:ecm:qa';
const REQUIRED_ADVANCED_GROUP_ALIASES: Array<{ alias: string; type: AliasType }> = [
  { alias: META_ECM_LCM, type: 'group' },
  { alias: META_ECM_QA, type: 'group' },
  { alias: TOOL_GROUP_NOTIFICATIONS_VIA_OUTLOOK, type: 'group' },
  { alias: TOOL_GROUP_BILLING_ADMIN, type: 'group' },
  { alias: TOOL_GROUP_SUPPORT_TICKETS, type: 'group' },
  { alias: TOOL_GROUP_DRUGBANK, type: 'group' }
];
const DOCUMENTS_LIBRARY_TOOL_ALIASES = [
  TOOL_GROUP_DOC_LIB_EXTERNAL,
  TOOL_GROUP_DOC_LIB_INTERNAL,
  TOOL_GROUP_DOC_LIB_ADMIN,
  TOOL_ALIAS_DOC_LIB_EXTERNAL,
  TOOL_ALIAS_DOC_LIB_INTERNAL,
  TOOL_ALIAS_DOC_LIB_ADMIN,
  TOOL_ALIAS_LEGACY_DOCUMENTS_LIBRARY,
  TOOL_ALIAS_LEGACY_DOCUMENTS_LIBRARY_WRITE
];
const LAI_JAIL_TOOL_ALIASES = [
  TOOL_GROUP_LAI_JAIL_READ,
  TOOL_GROUP_LAI_JAIL_WRITE,
  TOOL_ALIAS_LAI_JAIL,
  TOOL_ALIAS_LAI_JAIL_WRITE
];
const MEDCALL_TOOL_ALIASES = [
  TOOL_GROUP_MEDCALL_STANDARD,
  TOOL_GROUP_MEDCALL_POWER_USER,
  TOOL_ALIAS_MEDCALL_STANDARD,
  TOOL_ALIAS_LEGACY_MEDCALL,
  TOOL_ALIAS_MEDCALL_POWER_USER
];
const NOTIFICATION_LOG_TOOL_ALIASES = [
  TOOL_GROUP_NOTIFICATION_LOG,
  TOOL_ALIAS_LEGACY_NOTIFICATION_LOG,
  TOOL_ALIAS_NOTIFICATION_LOG
];
const REFERRAL_TOOL_ALIASES = [
  TOOL_GROUP_REFERRAL_INTAKE,
  TOOL_GROUP_LEGACY_REFERRAL_TOOL,
  TOOL_ALIAS_PUBLIC_DEFENDERS_LIST,
  TOOL_ALIAS_REFERRAL_INTAKE,
  TOOL_ALIAS_LEGACY_REFERRAL_TOOL
];
const USER_ACCOUNT_REQUEST_TOOL_ALIASES = [
  TOOL_GROUP_USER_ACCOUNT_REQUESTS,
  TOOL_ALIAS_USER_ACCOUNT_REQUESTS,
  'tools:requestuser~full'
];
const BILLING_TOOL_ALIASES = [
    TOOL_GROUP_BILLING,
    'billing',
    TOOL_GROUP_BILLING_ADMIN,
    'Billing',
    'ecm:dashboard',
    'ecm:dashboard:super',
    'ecm:outreach',
    'ecmdashboard',
    'ecmdashboardaccess',
    'ecmdashboardsuper',
    'ecmdashboardsuperaccess',
    'ecmoutreach',
    'ecmoutreachaccess',
    'legacyecmdashboard',
    'legacyecmdashboardaccess',
    'miftools',
    'miftoolsaccess',
    'providermedicaldemographics',
    'providermedicaldemographicsaccess'
];

const normalizePolicyKey = (value: unknown) => `${value || ''}`.trim().toLowerCase();
const SIMPLE_VIEW_ALIAS_VALUES = new Set([
  'user',
  ...DOCUMENTS_LIBRARY_TOOL_ALIASES,
  ...LAI_JAIL_TOOL_ALIASES,
  ...MEDCALL_TOOL_ALIASES,
  ...NOTIFICATION_LOG_TOOL_ALIASES,
  ...REFERRAL_TOOL_ALIASES,
  ...USER_ACCOUNT_REQUEST_TOOL_ALIASES,
  ...BILLING_TOOL_ALIASES
].map((value) => normalizePolicyKey(value)));
const normalizePolicyList = (values: string[]) => {
  const normalizedValues: string[] = [];
  const seen = new Set<string>();

  values.forEach((value) => {
    const trimmed = `${value || ''}`.trim();
    if (!trimmed) {
      return;
    }

    const key = normalizePolicyKey(trimmed);
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    normalizedValues.push(trimmed);
  });

  return normalizedValues;
};

const addPolicyValue = (values: string[], nextValue: string) => {
  const normalizedNext = normalizePolicyKey(nextValue);
  if (!normalizedNext) {
    return values;
  }

  const hasValue = values.some((value) => normalizePolicyKey(value) === normalizedNext);
  if (hasValue) {
    return values;
  }

  return [...values, nextValue];
};

const removePolicyValue = (values: string[], valueToRemove: string) => {
  const normalizedRemove = normalizePolicyKey(valueToRemove);
  if (!normalizedRemove) {
    return values;
  }

  return values.filter((value) => normalizePolicyKey(value) !== normalizedRemove);
};

const removePolicyValues = (values: string[], valuesToRemove: string[]) => {
  let next = values;
  valuesToRemove.forEach((value) => {
    next = removePolicyValue(next, value);
  });
  return next;
};

const compactPolicyKey = (value: unknown) =>
  `${value || ''}`.trim().toLowerCase().replace(/_/g, '').replace(/\s+/g, '');

const isOrganizationalAliasName = (value: unknown) => {
  const parts = `${value || ''}`
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length === 2;
};

const normalizeAliasType = (value: unknown): AliasType | '' => {
  const normalized = `${value || ''}`.trim().toLowerCase();
  if (
    normalized === 'group' ||
    normalized === 'policy' ||
    normalized === 'legacy' ||
    normalized === 'hidden' ||
    normalized === 'organizational'
  ) {
    return normalized;
  }
  return '';
};

const resolveAliasType = (alias: AliasRecord): AliasType => {
  const explicitType = normalizeAliasType(alias?.type);
  if (explicitType) {
    return explicitType;
  }
  const normalizedAlias = `${alias?.alias || ''}`.trim().toLowerCase();
  if (normalizedAlias.startsWith('legacy:')) {
    return 'legacy';
  }
  if (normalizedAlias.startsWith('hidden:')) {
    return 'hidden';
  }
  if (isOrganizationalAliasName(alias?.alias)) {
    return 'organizational';
  }
  return 'group';
};

const isExcludedRoleAssignment = (value: unknown, aliasType?: AliasType) => {
  if (aliasType === 'legacy' || aliasType === 'hidden' || aliasType === 'organizational') {
    return true;
  }

  const normalized = `${value || ''}`.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  if (normalized.startsWith('legacy:') || isOrganizationalAliasName(value)) {
    return true;
  }

  const compact = compactPolicyKey(value);
  if (LEGACY_ROLE_ALIAS_VALUES.has(compact)) {
    return true;
  }

  return !normalized.includes(':') && (
    compact.endsWith('access') ||
    compact.endsWith('acess') ||
    compact.endsWith('editparticipant')
  );
};

const categoryOrder: Record<PolicyCategory, number> = {
  group: 0,
  policy: 1,
  direct: 2
};

const categoryLabel: Record<PolicyCategory, string> = {
  group: 'Group',
  policy: 'Feature',
  direct: 'Direct Feature'
};

const categoryBadgeClass: Record<PolicyCategory, string> = {
  group: 'border border-blue-200 bg-blue-100 text-blue-800',
  policy: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
  direct: 'border border-gray-200 bg-gray-100 text-gray-700'
};

interface UserSecurityPermissionsManagerProps {
  username: string;
  simplePermissions?: React.ReactNode;
  assignedPolicies?: string[];
  onChangeAssignedPolicies?: (policies: string[]) => Promise<void> | void;
  headerContent?: React.ReactNode;
  headerActions?: React.ReactNode;
  tabsPosition?: 'left' | 'right';
  showSectionHeader?: boolean;
  sectionTitle?: string;
  sectionDescription?: string;
}

export const UserSecurityPermissionsManager: React.FC<UserSecurityPermissionsManagerProps> = ({
  username,
  simplePermissions,
  assignedPolicies: controlledAssignedPolicies,
  onChangeAssignedPolicies,
  headerContent,
  headerActions,
  tabsPosition = 'left',
  showSectionHeader = false,
  sectionTitle = 'Permissions',
  sectionDescription = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<'type' | 'name'>('type');
  const [favoritePolicies, setFavoritePolicies] = useState<string[]>([]);
  const [localAssignedPolicies, setLocalAssignedPolicies] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const isControlled = typeof onChangeAssignedPolicies === 'function';

  const { data: aliasData } = useQuery<{ listPolicyAliases: AliasRecord[] }>(
    LIST_POLICY_ALIASES,
    { fetchPolicy: 'cache-first' }
  );
  const { data: userPoliciesData } = useQuery<{
    listUserSecurityPolicies: UserPolicyAssignment[];
  }>(LIST_USER_SECURITY_POLICIES, {
    variables: { username },
    skip: !username || isControlled
  });

  const [updatePolicies] = useMutation(UPDATE_USER_SECURITY_POLICIES);

  useEffect(() => {
    if (isControlled) {
      return;
    }

    const rows = userPoliciesData?.listUserSecurityPolicies;
    if (!Array.isArray(rows)) {
      return;
    }
    const next = normalizePolicyList(rows
      .filter((row) => row?.allow !== false)
      .map((row) => `${row.policy || ''}`.trim())
      .filter((policy) => policy.length > 0));
    setLocalAssignedPolicies(next);
  }, [isControlled, userPoliciesData]);

  const assignedPolicies = useMemo(() => {
    if (isControlled) {
      return normalizePolicyList(controlledAssignedPolicies || []);
    }

    return localAssignedPolicies;
  }, [controlledAssignedPolicies, isControlled, localAssignedPolicies]);

  const aliasRecords = useMemo(() => {
    const aliases = aliasData?.listPolicyAliases;
    const records = Array.isArray(aliases)
      ? aliases
          .filter((alias) => alias?.active !== false)
          .map((alias) => ({
            alias: alias.alias,
            type: resolveAliasType(alias)
          }))
          .filter((alias) => `${alias.alias || ''}`.trim().length > 0)
      : [];
    const recordKeys = new Set(records.map((alias) => normalizePolicyKey(alias.alias)));

    REQUIRED_ADVANCED_GROUP_ALIASES.forEach((alias) => {
      const aliasKey = normalizePolicyKey(alias.alias);
      if (!aliasKey || recordKeys.has(aliasKey)) {
        return;
      }
      records.push(alias);
      recordKeys.add(aliasKey);
    });

    return records.sort((left, right) => left.alias.localeCompare(right.alias));
  }, [aliasData]);

  const aliasTypeByName = useMemo(() => {
    return new Map(aliasRecords.map((alias) => [alias.alias, alias.type]));
  }, [aliasRecords]);

  const aliasOptions = useMemo(() => {
    return aliasRecords
      .filter((alias) => visibleAliasTypes.has(alias.type))
      .map((alias) => alias.alias);
  }, [aliasRecords]);

  const availableAliases = useMemo(() => {
    if (aliasOptions.length === 0) {
      return [];
    }
    return aliasOptions.filter((alias) => {
      if (SIMPLE_VIEW_ALIAS_VALUES.has(normalizePolicyKey(alias))) {
        return false;
      }
      return !assignedPolicies.includes(alias);
    });
  }, [aliasOptions, assignedPolicies]);

  const availableItems = useMemo(() => {
    return availableAliases.map((alias) => ({
      value: alias,
      category: (aliasTypeByName.get(alias) || 'group') as PolicyCategory
    }));
  }, [aliasTypeByName, availableAliases]);

  const assignedItems = useMemo(() => {
    const seen = new Set<string>();
    return assignedPolicies
      .map((policy) => {
        const aliasType = aliasTypeByName.get(policy);
        if (isExcludedRoleAssignment(policy, aliasType)) {
          return null;
        }

        return {
          value: policy,
          category: (aliasType && visibleAliasTypes.has(aliasType) ? aliasType : 'direct') as PolicyCategory
        };
      })
      .filter((item): item is { value: string; category: PolicyCategory } => {
        if (!item) {
          return false;
        }
        const key = `${item.category}:${item.value}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }, [assignedPolicies, aliasTypeByName]);

  const visibleAssignedItems = useMemo(() => {
    return assignedItems;
  }, [assignedItems]);

  const filteredAvailableItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const hasTerm = term.length > 0;
    return availableItems.filter((item) => {
      if (!hasTerm) {
        return true;
      }
      if (item.value.toLowerCase().includes(term)) {
        return true;
      }
      return false;
    });
  }, [availableItems, searchTerm]);

  const favoriteSet = useMemo(() => new Set(favoritePolicies), [favoritePolicies]);

  const sortItems = React.useCallback((items: { value: string; category: PolicyCategory }[]) => {
    const sorted = [...items];
    sorted.sort((a, b) => {
      const aFavorite = favoriteSet.has(a.value);
      const bFavorite = favoriteSet.has(b.value);
      if (aFavorite && !bFavorite) {
        return -1;
      }
      if (!aFavorite && bFavorite) {
        return 1;
      }
      if (sortMode === 'name') {
        return a.value.localeCompare(b.value);
      }
      const orderDiff = categoryOrder[a.category] - categoryOrder[b.category];
      if (orderDiff !== 0) {
        return orderDiff;
      }
      return a.value.localeCompare(b.value);
    });
    return sorted;
  }, [favoriteSet, sortMode]);

  const sortedAvailableItems = useMemo(
    () => sortItems(filteredAvailableItems),
    [filteredAvailableItems, sortItems]
  );
  const sortedAssignedItems = useMemo(
    () => sortItems(visibleAssignedItems),
    [sortItems, visibleAssignedItems]
  );

  const assignedPolicySet = useMemo(() => {
    return new Set(assignedPolicies.map((policy) => normalizePolicyKey(policy)));
  }, [assignedPolicies]);

  const saveFeatures = async (nextFeatures: string[]) => {
    const normalizedNextFeatures = normalizePolicyList(nextFeatures);

    if (isControlled) {
      if (!onChangeAssignedPolicies) {
        return;
      }

      setIsSaving(true);
      try {
        await onChangeAssignedPolicies(normalizedNextFeatures);
      } catch (error) {
        console.error('Failed to update security features:', error);
        toast.error('Failed to update security features');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!username) {
      return;
    }

    const previousFeatures = assignedPolicies;
    setLocalAssignedPolicies(normalizedNextFeatures);
    setIsSaving(true);

    try {
      const payload = normalizedNextFeatures.map((policy) => ({
        policy,
        allow: true
      }));
      const result = await updatePolicies({
        variables: { username, policies: payload }
      });
      if (!result?.data?.updateUserSecurityPolicies?.success) {
        setLocalAssignedPolicies(previousFeatures);
        toast.error(result?.data?.updateUserSecurityPolicies?.message || 'Failed to update features');
        return;
      }
      toast.success('Security features updated');
    } catch (error) {
      setLocalAssignedPolicies(previousFeatures);
      console.error('Failed to update security features:', error);
      toast.error('Failed to update security features');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignFeature = async (feature: string) => {
    const nextFeature = `${feature || ''}`.trim();
    if (!nextFeature) {
      return;
    }
    if (assignedPolicies.includes(nextFeature)) {
      return;
    }
    const next = [...assignedPolicies, nextFeature];
    await saveFeatures(next);
  };

  const handleRemoveFeature = async (feature: string) => {
    const next = assignedPolicies.filter((item) => item !== feature);
    await saveFeatures(next);
  };

  const handleToggleFavorite = (value: string) => {
    const nextValue = `${value || ''}`.trim();
    if (!nextValue) {
      return;
    }
    setFavoritePolicies((prev) => {
      if (prev.includes(nextValue)) {
        return prev.filter((item) => item !== nextValue);
      }
      return [...prev, nextValue];
    });
  };

  const getDisplayLabel = (value: string) => value;

  const hasAssignedPolicy = (policy: string) => assignedPolicySet.has(normalizePolicyKey(policy));

  const medcallPowerEnabled = hasAssignedPolicy(TOOL_GROUP_MEDCALL_POWER_USER);
  const medcallEnabled =
    medcallPowerEnabled ||
    hasAssignedPolicy(TOOL_GROUP_MEDCALL_STANDARD);
  const documentsLibraryAdminEnabled =
    hasAssignedPolicy(TOOL_GROUP_DOC_LIB_ADMIN) ||
    hasAssignedPolicy(TOOL_ALIAS_DOC_LIB_ADMIN) ||
    hasAssignedPolicy(TOOL_ALIAS_LEGACY_DOCUMENTS_LIBRARY_WRITE);
  const documentsLibraryInternalEnabled =
    documentsLibraryAdminEnabled ||
    hasAssignedPolicy(TOOL_GROUP_DOC_LIB_INTERNAL) ||
    hasAssignedPolicy(TOOL_ALIAS_DOC_LIB_INTERNAL) ||
    hasAssignedPolicy(TOOL_ALIAS_LEGACY_DOCUMENTS_LIBRARY);
  const documentsLibraryExternalEnabled =
    documentsLibraryInternalEnabled ||
    hasAssignedPolicy(TOOL_GROUP_DOC_LIB_EXTERNAL) ||
    hasAssignedPolicy(TOOL_ALIAS_DOC_LIB_EXTERNAL);
  const laiJailReadPoliciesEnabled =
    hasAssignedPolicy(TOOL_GROUP_LAI_JAIL_READ) ||
    hasAssignedPolicy(TOOL_ALIAS_LAI_JAIL);
  const laiJailWriteEnabled =
    hasAssignedPolicy(TOOL_GROUP_LAI_JAIL_WRITE) ||
    hasAssignedPolicy(TOOL_ALIAS_LAI_JAIL_WRITE);
  const laiJailEnabled =
    laiJailWriteEnabled ||
    laiJailReadPoliciesEnabled ||
    hasAssignedPolicy(TOOL_ALIAS_LAI_JAIL);
  const referralIntakeEnabled =
    hasAssignedPolicy(TOOL_GROUP_REFERRAL_INTAKE) ||
    hasAssignedPolicy(TOOL_GROUP_LEGACY_REFERRAL_TOOL) ||
    hasAssignedPolicy(TOOL_ALIAS_PUBLIC_DEFENDERS_LIST) ||
    hasAssignedPolicy(TOOL_ALIAS_REFERRAL_INTAKE) ||
    hasAssignedPolicy(TOOL_ALIAS_LEGACY_REFERRAL_TOOL);
  const notificationLogEnabled =
    hasAssignedPolicy(TOOL_GROUP_NOTIFICATION_LOG) ||
    hasAssignedPolicy(TOOL_ALIAS_NOTIFICATION_LOG);
  const userAccountRequestsEnabled =
    hasAssignedPolicy(TOOL_GROUP_USER_ACCOUNT_REQUESTS) ||
    hasAssignedPolicy(TOOL_ALIAS_USER_ACCOUNT_REQUESTS);
  const billingEnabled =
    hasAssignedPolicy(TOOL_GROUP_BILLING) ||
    hasAssignedPolicy(TOOL_GROUP_BILLING_ADMIN) ||
    hasAssignedPolicy('Billing');

  const applyToolPolicyUpdate = async (updater: (current: string[]) => string[]) => {
    const next = updater(assignedPolicies);
    await saveFeatures(next);
  };

  const handleToggleDocumentsLibraryExternal = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, DOCUMENTS_LIBRARY_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_DOC_LIB_EXTERNAL);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, DOCUMENTS_LIBRARY_TOOL_ALIASES));
  };

  const handleToggleDocumentsLibraryInternal = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, DOCUMENTS_LIBRARY_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_DOC_LIB_INTERNAL);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => {
      const next = removePolicyValues(current, DOCUMENTS_LIBRARY_TOOL_ALIASES);
      return addPolicyValue(next, TOOL_GROUP_DOC_LIB_EXTERNAL);
    });
  };

  const handleToggleDocumentsLibraryAdmin = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, DOCUMENTS_LIBRARY_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_DOC_LIB_ADMIN);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => {
      const next = removePolicyValues(current, DOCUMENTS_LIBRARY_TOOL_ALIASES);
      return addPolicyValue(next, TOOL_GROUP_DOC_LIB_INTERNAL);
    });
  };

  const handleToggleLaiJail = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, LAI_JAIL_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_LAI_JAIL_READ);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, LAI_JAIL_TOOL_ALIASES));
  };

  const handleToggleLaiJailWrite = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, LAI_JAIL_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_LAI_JAIL_WRITE);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => {
      const next = removePolicyValues(current, LAI_JAIL_TOOL_ALIASES);
      return addPolicyValue(next, TOOL_GROUP_LAI_JAIL_READ);
    });
  };

  const handleToggleMedCall = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, MEDCALL_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_MEDCALL_STANDARD);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, MEDCALL_TOOL_ALIASES));
  };

  const handleToggleMedCallPowerUser = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, MEDCALL_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_MEDCALL_POWER_USER);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => {
      const next = removePolicyValues(current, MEDCALL_TOOL_ALIASES);
      return addPolicyValue(next, TOOL_GROUP_MEDCALL_STANDARD);
    });
  };

  const handleToggleReferralTool = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, REFERRAL_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_REFERRAL_INTAKE);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, REFERRAL_TOOL_ALIASES));
  };

  const handleToggleNotificationLog = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, NOTIFICATION_LOG_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_NOTIFICATION_LOG);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, NOTIFICATION_LOG_TOOL_ALIASES));
  };

  const handleToggleUserAccountRequests = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, USER_ACCOUNT_REQUEST_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_USER_ACCOUNT_REQUESTS);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, USER_ACCOUNT_REQUEST_TOOL_ALIASES));
  };

  const handleToggleBilling = async (enabled: boolean) => {
    if (enabled) {
      await applyToolPolicyUpdate((current) => {
        const next = removePolicyValues(current, BILLING_TOOL_ALIASES);
        return addPolicyValue(next, TOOL_GROUP_BILLING);
      });
      return;
    }

    await applyToolPolicyUpdate((current) => removePolicyValues(current, BILLING_TOOL_ALIASES));
  };

  const inlineToolItems = [
    {
      key: 'notification-log',
      label: 'Notification Log',
      checked: notificationLogEnabled,
      onChange: handleToggleNotificationLog
    },
    {
      key: 'referral-tool',
      label: 'Public Defenders List',
      checked: referralIntakeEnabled,
      onChange: handleToggleReferralTool
    },
    {
      key: 'billing',
      label: 'Billing',
      checked: billingEnabled,
      onChange: handleToggleBilling
    }
  ];

  const userAccountRequestTool = {
    key: 'user-account-requests',
    label: 'User Account Requests',
    checked: userAccountRequestsEnabled,
    onChange: handleToggleUserAccountRequests
  };

  const toolColumns = [
    [
      {
        key: 'documents-library-external',
        label: 'Doc Library External',
        checked: documentsLibraryExternalEnabled,
        onChange: handleToggleDocumentsLibraryExternal
      },
      {
        key: 'documents-library-internal',
        label: 'Doc Library Internal',
        checked: documentsLibraryInternalEnabled,
        onChange: handleToggleDocumentsLibraryInternal
      },
      {
        key: 'documents-library-admin',
        label: 'Doc Library Admin',
        checked: documentsLibraryAdminEnabled,
        onChange: handleToggleDocumentsLibraryAdmin
      }
    ],
    [
      {
        key: 'lai-jail',
        label: 'LAI Jail',
        checked: laiJailEnabled,
        onChange: handleToggleLaiJail
      },
      {
        key: 'lai-jail-write',
        label: 'LAI Jail (write)',
        checked: laiJailWriteEnabled,
        onChange: handleToggleLaiJailWrite
      }
    ],
    [
      {
        key: 'medcall',
        label: 'MedCall Standard',
        checked: medcallEnabled,
        onChange: handleToggleMedCall
      },
      {
        key: 'medcall-power',
        label: 'MedCall Power User',
        checked: medcallPowerEnabled,
        onChange: handleToggleMedCallPowerUser
      }
    ]
  ];

  const renderToolCheckbox = (tool: {
    key: string;
    label: string;
    checked: boolean;
    onChange: (enabled: boolean) => void;
  }) => (
    <label key={tool.key} className="flex items-center gap-2 text-sm text-gray-900">
      <input
        type="checkbox"
        data-testid={`security-tool-${tool.key}`}
        checked={tool.checked}
        disabled={isSaving}
        onChange={(event) => tool.onChange(event.target.checked)}
      />
      <span>{tool.label}</span>
    </label>
  );

  const toolsPanelControls = (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-5">
        {renderToolCheckbox(userAccountRequestTool)}
      </div>
      <div className="border-t border-gray-100 pt-3">
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-4">
          {toolColumns.map((column, columnIndex) => (
            <div key={`tool-column-${columnIndex}`} className="space-y-2">
              {column.map((tool) => renderToolCheckbox(tool))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const inlineToolsPanel = (
    <>
      {inlineToolItems.map((tool) => renderToolCheckbox(tool))}
    </>
  );

  const standaloneToolsPanel = (
    <section className="space-y-3" data-testid="security-tools-section">
      <p className="text-sm font-bold text-gray-900">Tools</p>
      <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-5">
        {inlineToolsPanel}
      </div>
      {toolsPanelControls}
    </section>
  );

  const integratedToolsPanel = (
    <div data-testid="security-tools-section">
      {toolsPanelControls}
    </div>
  );

  const groupsAndFeaturesPanel = (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search groups and features"
              className="h-8 min-w-0 w-full bg-white text-sm"
            />
            <div className="flex items-center gap-1 justify-self-end text-[10px] text-gray-600">
              <span>Sort</span>
              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value as 'type' | 'name')}
                className="h-6 rounded-md border border-gray-200 bg-white px-1.5 text-[10px] leading-none text-gray-700"
              >
                <option value="type">Type</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <p className="px-1 text-xs font-medium text-gray-600">Available Groups and Features (click to add)</p>
            <div className="h-[44vh] overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
              {sortedAvailableItems.length === 0 && (
                <p className="px-2 py-1 text-xs text-gray-500">
                  No groups or features match this search.
                </p>
              )}
              {sortedAvailableItems.map((item) => (
                <div
                  key={item.value}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <button
                    type="button"
                    onClick={() => handleAssignFeature(item.value)}
                    disabled={isSaving}
                    className="flex min-w-0 flex-1 items-center text-left disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1 truncate">{getDisplayLabel(item.value)}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleFavorite(item.value)}
                    disabled={isSaving}
                    className="shrink-0 text-gray-300 hover:text-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={`Toggle favorite for ${getDisplayLabel(item.value)}`}
                  >
                    {favoriteSet.has(item.value) ? (
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                    ) : (
                      <StarOff className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <span
                    className={`shrink-0 rounded-sm px-1 py-0 text-xs font-semibold leading-4 ${categoryBadgeClass[item.category]}`}
                  >
                    {categoryLabel[item.category]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="px-1 text-xs font-medium text-gray-600">Assigned Access (click to remove)</p>
            <div className="h-[44vh] overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
              {sortedAssignedItems.length === 0 && (
                <p className="px-2 py-1 text-xs text-gray-500">
                  No aliases assigned.
                </p>
              )}
              {sortedAssignedItems.map((item) => (
                <div
                  key={item.value}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(item.value)}
                    disabled={isSaving}
                    className="flex min-w-0 flex-1 items-center text-left disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1 truncate">{getDisplayLabel(item.value)}</span>
                  </button>
                  <span
                    className={`shrink-0 rounded-sm px-1 py-0 text-xs font-semibold leading-4 ${categoryBadgeClass[item.category]}`}
                  >
                    {categoryLabel[item.category]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!simplePermissions) {
    return groupsAndFeaturesPanel;
  }

  const canInjectToolsIntoSimplePermissions =
    React.isValidElement(simplePermissions) && typeof simplePermissions.type !== 'string';
  const simplePermissionsWithIntegratedTools = canInjectToolsIntoSimplePermissions
    ? React.cloneElement(simplePermissions as React.ReactElement<any>, {
        inlineToolsPanel,
        integratedToolsPanel
      })
    : simplePermissions;

  const tabsList = (
    <TabsList
      className={`inline-flex h-6 items-center rounded-md border border-gray-300 bg-white p-px shadow-sm ${
        tabsPosition === 'right' ? 'ml-auto' : ''
      }`}
    >
      <TabsTrigger
        className="h-5 rounded-sm px-2 text-[11px] font-semibold leading-none text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=inactive]:hover:bg-gray-100"
        value="database"
      >
        Simple View
      </TabsTrigger>
      <TabsTrigger
        className="h-5 rounded-sm px-2 text-[11px] font-semibold leading-none text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=inactive]:hover:bg-gray-100"
        value="groups-features"
      >
        Advanced
      </TabsTrigger>
    </TabsList>
  );

  const resolvedHeaderContent = headerContent || (showSectionHeader ? (
    <div>
      <p className="text-sm font-semibold text-gray-900">{sectionTitle}</p>
      {`${sectionDescription || ''}`.trim().length > 0 && (
        <p className="mt-1 text-xs text-gray-600">{sectionDescription}</p>
      )}
    </div>
  ) : null);

  return (
    <Tabs defaultValue="database" className="space-y-0">
      <div className="rounded-md border border-gray-200 bg-white p-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {tabsPosition !== 'right' && tabsList}
          {(resolvedHeaderContent || headerActions) && (
            <>
              <div className="min-w-0 flex-1">{resolvedHeaderContent}</div>
              {headerActions}
            </>
          )}
          {tabsPosition === 'right' && tabsList}
        </div>
        <TabsContent className="mt-0" value="groups-features">
          {groupsAndFeaturesPanel}
        </TabsContent>
        <TabsContent className="mt-0" value="database">
          <div className="space-y-6">
            {simplePermissionsWithIntegratedTools}
            {!canInjectToolsIntoSimplePermissions && standaloneToolsPanel}
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );
};
